import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Aperture } from "lucide-react";

const CAMPAIGNS = [
  {
    slug: "the-manifesto",
    issue: "Vol. 00",
    date: "Feb 2026",
    category: "The Master Poster",
    image: "/products/master-poster.jpg",
    headline: "Protection. Pleasure. Confidence.",
    headline_accent: "Pleasure.",
    sub_top: "Refined by Nature.",
    sub_bottom: "Perfected for You.",
    body:
      "The cornerstone composition that defines the BananaX brand world — Ananya Verma as the face of the edit, the four signature variants in their color-coded sleeves, the five-pillar promise of premium intimate wellness, and the closing line that holds them all together: Be Safe. Be Sure. Be Irresistible.",
    cta: { label: "Meet the collection", to: "/shop" },
  },
  {
    slug: "wrong-moment",
    issue: "Vol. 01",
    date: "Feb 2026",
    category: "Brand Manifesto",
    image: "/products/campaign-low-battery.jpg",
    headline: "Nothing should die at the wrong moment.",
    headline_accent: "wrong moment.",
    sub_top: "Power down the risks.",
    sub_bottom: "Power up your pleasure.",
    body:
      "The opening chapter of the BananaX campaign — a witty, sensual reminder that the smallest device should never decide the moment. Shot on warm honey-gold tungsten, lit like a bedside lantern, this is the conversation we wanted to start: protection that refuses to interrupt intimacy.",
    cta: { label: "Shop the Edit", to: "/shop" },
    behind_lens: {
      caption:
        "Shot over two nights in a custom-built suite on a velvet-draped bed. The lantern, the folded silk, the matte-black BananaX box on the nightstand — every prop was chosen to feel inherited rather than styled.",
      frames: [
        { src: "/products/btl-wrong-moment-1.jpg", label: "Nightstand still life" },
        { src: "/products/btl-wrong-moment-2.jpg", label: "Low battery, low light" },
        { src: "/products/btl-wrong-moment-3.jpg", label: "Golden hour bedside" },
      ],
    },
  },
  {
    slug: "extra-protection",
    issue: "Vol. 02",
    date: "Mar 2026",
    category: "Real Life Protection",
    image: "/products/campaign-password-protected.jpg",
    headline: "Some things deserve extra protection.",
    headline_accent: "extra",
    sub_top: "Not just in your phone.",
    sub_bottom: "In real life too.",
    body:
      "The second chapter steps closer — quieter, more familiar, more honest. Every BananaX condom is electronically tested, dermatologically certified, and crafted from premium natural latex — so the only thing that gets locked away is the worry.",
    cta: { label: "Our craft, our promise", to: "/about" },
    behind_lens: {
      caption:
        "Captured at 2am with a single brass-handled lantern as the only practical light source. We wanted the warmth of a real bedroom, not a studio. The phone is the visual rhyme — protection in your pocket, protection in real life.",
      frames: [
        { src: "/products/btl-extra-protection-1.jpg", label: "Macro of the lock" },
        { src: "/products/btl-extra-protection-2.jpg", label: "Box at lantern" },
        { src: "/products/btl-extra-protection-3.jpg", label: "Silk in tungsten" },
      ],
    },
  },
];

export default function Campaigns() {
  return (
    <div className="pt-32 pb-24 min-h-screen bg-ink-950" data-testid="campaigns-page">
      {/* Header */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="label-eyebrow mb-4">The BananaX Campaigns</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95]">
            Chapters of <span className="italic text-gradient-gold">intimacy</span>,
            <br />unfiltered.
          </h1>
          <p className="text-mist font-light leading-relaxed text-lg mt-8 max-w-2xl">
            Editorial moments &mdash; warm tungsten light, honest bodies, and a brand
            that refuses to whisper. Each campaign is a chapter in the ongoing BananaX
            story of refined intimate wellness.
          </p>
        </motion.div>
      </div>

      {/* Campaigns */}
      <div className="space-y-32 lg:space-y-48">
        {CAMPAIGNS.map((c, i) => (
          <CampaignBlock key={c.slug} campaign={c} reverse={i % 2 === 1} index={i} />
        ))}
      </div>

      {/* Coming soon */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 mt-32 lg:mt-48">
        <div className="border-t border-white/10 pt-16 text-center">
          <div className="label-eyebrow mb-4">Vol. 03 · Coming Soon</div>
          <h3 className="font-serif text-3xl md:text-5xl tracking-tight italic text-gold/80">
            "Built for the morning after."
          </h3>
          <p className="text-mist font-light leading-relaxed mt-6 max-w-xl mx-auto">
            The next chapter arrives this season. Join the BananaX circle to be the
            first to see the campaign film.
          </p>
          <Link to="/subscription" className="btn-ghost mt-8 inline-flex">
            Join the circle <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function CampaignBlock({ campaign, reverse, index }) {
  const headlineWithAccent = (() => {
    if (!campaign.headline_accent) return <>{campaign.headline}</>;
    const idx = campaign.headline.toLowerCase().lastIndexOf(campaign.headline_accent.toLowerCase());
    if (idx === -1) return <>{campaign.headline}</>;
    const before = campaign.headline.slice(0, idx);
    const accent = campaign.headline.slice(idx, idx + campaign.headline_accent.length);
    const after = campaign.headline.slice(idx + campaign.headline_accent.length);
    return (
      <>
        {before}
        <span className="italic text-gradient-gold">{accent}</span>
        {after}
      </>
    );
  })();

  return (
    <section
      className="max-w-[1600px] mx-auto px-6 md:px-10"
      data-testid={`campaign-block-${campaign.slug}`}
    >
      <div className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1 }}
          className="lg:col-span-7 relative"
          data-testid={`campaign-image-${campaign.slug}`}
        >
          <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden border hairline-gold">
            <img
              src={campaign.image}
              alt={campaign.headline}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/40 via-transparent to-transparent pointer-events-none"></div>
          </div>
          {/* Corner accents */}
          <div className="absolute -bottom-3 -right-3 w-24 h-24 border border-gold/30 hidden md:block"></div>
          <div className="absolute -top-3 -left-3 w-16 h-16 bg-gold/10 hidden md:block"></div>
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-5 space-y-7"
        >
          <div className="flex items-center gap-4 text-mist">
            <span className="label-eyebrow text-gold">{campaign.issue}</span>
            <span className="w-6 h-px bg-mist/30"></span>
            <span className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase">
              <Calendar className="w-3 h-3" />
              {campaign.date}
            </span>
          </div>

          <p className="label-eyebrow">{campaign.category}</p>

          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-bone">
            {headlineWithAccent}
          </h2>

          <div className="w-32 h-px bg-gold/50"></div>

          <div className="space-y-1">
            <p className="text-bone text-xl font-light">{campaign.sub_top}</p>
            <p className="text-gold text-xl font-light">{campaign.sub_bottom}</p>
          </div>

          <p className="text-mist font-light leading-relaxed text-base">
            {campaign.body}
          </p>

          <Link
            to={campaign.cta.to}
            className="inline-flex items-center gap-3 text-gold tracking-[0.22em] uppercase text-xs hover:text-gold-soft pt-2"
            data-testid={`campaign-cta-${campaign.slug}`}
          >
            {campaign.cta.label} <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>

      {/* Behind the Lens */}
      {campaign.behind_lens && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mt-20 lg:mt-28 border-t border-white/10 pt-14"
          data-testid={`campaign-btl-${campaign.slug}`}
        >
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4 space-y-4">
              <div className="label-eyebrow flex items-center gap-3">
                <Aperture className="w-3.5 h-3.5 text-gold" />
                Behind the Lens
              </div>
              <h3 className="font-serif text-2xl md:text-3xl leading-tight text-bone">
                From the set of <span className="italic text-gold">{campaign.issue}</span>.
              </h3>
              <p className="text-mist font-light leading-relaxed text-sm">
                {campaign.behind_lens.caption}
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-3 gap-3 lg:gap-5">
              {campaign.behind_lens.frames.map((f, j) => (
                <motion.figure
                  key={f.src}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: j * 0.1 }}
                  className="group relative overflow-hidden border hairline"
                  data-testid={`btl-frame-${campaign.slug}-${j}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={f.src}
                      alt={f.label}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink-950/90 via-ink-950/40 to-transparent p-3 pt-10">
                    <figcaption className="label-eyebrow text-bone/90">
                      Frame {String(j + 1).padStart(2, "0")} · {f.label}
                    </figcaption>
                  </div>
                </motion.figure>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </section>
  );
}
